import { z } from "zod";
import { validateNodeName } from "./tree";

/**
 * Construtor de sistemas com IA: a conversa produz um "blueprint" (arquitetura + arquivos)
 * que vira um projeto de verdade. Este é o contrato entre o serviço de IA, a API e o front.
 */
export const BUILDER_LIMITS = {
  maxFiles: 40,
  maxFileChars: 60_000,
  maxTotalChars: 400_000,
  maxPathLength: 200,
} as const;

/** Caminho relativo, com segmentos válidos para a árvore de arquivos (sem "..", sem barra inicial). */
export function validateProjectPath(path: string): string | null {
  if (!path || path.length > BUILDER_LIMITS.maxPathLength) return "caminho vazio ou longo demais";
  if (path.startsWith("/") || path.endsWith("/"))
    return "use um caminho relativo, sem barra no início ou no fim";
  for (const segment of path.split("/")) {
    const error = validateNodeName(segment);
    if (error) return `"${segment}": ${error}`;
    if (segment !== segment.trim()) return `"${segment}": sem espaços nas pontas`;
  }
  return null;
}

export const ProjectFileInput = z.object({
  path: z.string().superRefine((p, ctx) => {
    const error = validateProjectPath(p);
    if (error) ctx.addIssue({ code: "custom", message: error });
  }),
  content: z.string().max(BUILDER_LIMITS.maxFileChars),
});
export type ProjectFileInput = z.infer<typeof ProjectFileInput>;

/** Lista de arquivos de um projeto novo: caminhos únicos, sem arquivo e pasta com o mesmo nome. */
export const ProjectFilesInput = z
  .array(ProjectFileInput)
  .min(1)
  .max(BUILDER_LIMITS.maxFiles)
  .superRefine((files, ctx) => {
    const paths = new Set<string>();
    const folders = new Set<string>();
    let total = 0;
    for (const f of files) {
      total += f.content.length;
      if (paths.has(f.path)) ctx.addIssue({ code: "custom", message: `arquivo repetido: ${f.path}` });
      paths.add(f.path);
      const parts = f.path.split("/");
      for (let i = 1; i < parts.length; i++) folders.add(parts.slice(0, i).join("/"));
    }
    for (const p of paths) {
      if (folders.has(p))
        ctx.addIssue({ code: "custom", message: `"${p}" é arquivo e pasta ao mesmo tempo` });
    }
    if (total > BUILDER_LIMITS.maxTotalChars) {
      ctx.addIssue({ code: "custom", message: "o projeto gerado passou do tamanho máximo" });
    }
  });

export const Blueprint = z
  .object({
    name: z.string().trim().min(1).max(80),
    summary: z.string().trim().min(1).max(1_200),
    runtime: z.enum(["node", "python", "java"]),
    runCommand: z.string().max(200),
    stack: z
      .array(z.object({ layer: z.string().max(40), choice: z.string().max(80), reason: z.string().max(400) }))
      .max(12),
    components: z.array(z.object({ name: z.string().max(60), responsibility: z.string().max(400) })).max(16),
    files: z
      .array(ProjectFileInput.extend({ description: z.string().max(300) }))
      .min(1)
      .max(BUILDER_LIMITS.maxFiles),
    nextSteps: z.array(z.string().max(300)).max(8),
  })
  .superRefine((bp, ctx) => {
    // Mesmas regras de conjunto da criação de projeto (caminhos únicos, tamanho total).
    const files = ProjectFilesInput.safeParse(bp.files.map(({ path, content }) => ({ path, content })));
    if (files.success) return;
    for (const issue of files.error.issues)
      ctx.addIssue({ code: "custom", path: ["files"], message: issue.message });
  });
export type Blueprint = z.infer<typeof Blueprint>;

export const AiBuildInput = z.object({
  messages: z
    .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().min(1).max(20_000) }))
    .min(1)
    .max(40)
    .refine((m) => m[m.length - 1]?.role === "user", "a última mensagem precisa ser da pessoa"),
  /** Blueprint atual, para pedidos de ajuste ("troque o banco por SQLite"). */
  blueprint: z.unknown().optional(),
});
export type AiBuildInput = z.infer<typeof AiBuildInput>;

/** Eventos SSE de /ai/build. */
export type BuildEvent =
  | { type: "thinking"; text: string }
  | { type: "delta"; text: string }
  | { type: "blueprint_start" }
  | { type: "blueprint_progress"; chars: number }
  | { type: "blueprint"; blueprint: Blueprint }
  | { type: "done"; stopReason: string | null; model: string }
  | { type: "error"; code: string; message: string };
